<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {useGlobalEmitter} from "@/helper/emitter";
import {getRandomArrayElement} from "@/helper/array";

const model = ref(false)
useGlobalEmitter().on("FireFireworks", () => {
    model.value = false
    model.value = true
})

const props = withDefaults(defineProps<{
    duration?: number
    amount?: number
}>(), {
    duration: 10000,
    amount: 42,
})

const possibleColors = ['--ion-color-red', '--ion-color-green', '--ion-color-yellow', '--ion-color-black']
const maxDelayInMs = -3000
const heightDeviationInPercent = 80
const viewportGapInPercent = 15
const animationDurationInMs = ref(4000)
const root = ref()
const isActive = ref(false)
let durationTimeout: ReturnType<typeof setTimeout> | undefined

const fireworks = computed(() => {
    const effectiveSpace = 100 - viewportGapInPercent / 2
    const spaceBetween = effectiveSpace / props.amount
    const firstPosition = props.amount % 2 === 0 ? 50 - spaceBetween : 50

    return Array.from(Array(props.amount).keys()).map(index => {
        return {
            key: index,
            color: getRandomArrayElement(possibleColors),
            delay: Math.floor(Math.random() * (maxDelayInMs + 1)),
            height: Math.floor(Math.random() * (heightDeviationInPercent + 1) + heightDeviationInPercent),
            left: firstPosition + (index % 2 === 0 ? -(index * spaceBetween) : (Math.floor(index / 2) + 1) * spaceBetween)
        }
    })
})

const onAnimationEnd = (e: AnimationEvent) => {
    const target = e.currentTarget as HTMLElement

    if (!model.value) {
        target.classList.add('stopped')
    }
}

watch(() => model.value, () => {
    clearTimeout(durationTimeout)

    if (!model.value) {
        setTimeout(() => {
            if (!model.value) {
                isActive.value = false
            }
        }, animationDurationInMs.value)

        return
    }

    isActive.value = true

    for (const child of root.value.children) {
        child.classList.remove('stopped')
    }

    if (props.duration) {
        durationTimeout = setTimeout(() => {
            model.value = false
        }, props.duration)
    }
}, { immediate: true })




</script>

<template>
<div
    class="fireworks"
    ref="root"
    :class="{active: isActive}"
    :style="{'--duration': `${animationDurationInMs}ms`}"
    >
    <div
        v-for="firework in fireworks"
        class="firework"
        @animationiteration="onAnimationEnd"
        :style="{'--color': `var(${firework.color})`, '--delay': `${firework.delay}ms`, '--height': `${firework.height}%`, left: `${firework.left}%`}"
    />
</div>
</template>

<style lang="scss" scoped>
.fireworks {
    position: fixed;
    bottom: 0;
    height: 100vh;
    left: 0;
    width: 100vw;
    overflow: hidden;
    pointer-events: none;
    z-index: 1001;

    &.active {
        .firework,
        .firework::before,
        .firework::after {
            animation: firework var(--duration) infinite;
            animation-delay: calc(2s + var(--delay));
        }
    }
}

@keyframes firework {
    0% { 
        transform: translate(-50%, 60vh);
        width: 0.5vmin;
        opacity: 0;
    }
    50% { 
        width: 0.5vmin;
        opacity: 1;
    }
    100% { 
        width: 45vmin; 
        opacity: 0; 
    }
}

.firework,
.firework::before,
.firework::after {
    content: "";
    position: absolute;
    top: calc(150% - var(--height));
    left: 50%;
    transform: translate(-50%, -50%);
    width: 0.5vmin;
    aspect-ratio: 1;
    opacity: 0;
    background:
        /* random backgrounds */
        radial-gradient(circle, var(--color) 0.2vmin, #0000 0) 50% 00%,
        radial-gradient(circle, var(--color) 0.3vmin, #0000 0) 00% 50%,
        radial-gradient(circle, var(--color) 0.5vmin, #0000 0) 50% 99%,
        radial-gradient(circle, var(--color) 0.2vmin, #0000 0) 99% 50%,
        radial-gradient(circle, var(--color) 0.3vmin, #0000 0) 80% 90%,
        radial-gradient(circle, var(--color) 0.5vmin, #0000 0) 95% 90%,
        radial-gradient(circle, var(--color) 0.5vmin, #0000 0) 10% 60%,
        radial-gradient(circle, var(--color) 0.2vmin, #0000 0) 31% 80%,
        radial-gradient(circle, var(--color) 0.3vmin, #0000 0) 80% 10%,
        radial-gradient(circle, var(--color) 0.2vmin, #0000 0) 90% 23%,
        radial-gradient(circle, var(--color) 0.3vmin, #0000 0) 45% 20%,
        radial-gradient(circle, var(--color) 0.5vmin, #0000 0) 13% 24%
    ;
    background-size: 0.5vmin 0.5vmin;
    background-repeat: no-repeat;
}

.firework.stopped {
    animation: none !important;
    animation-iteration-count: 0 !important;
    animation-fill-mode: forwards !important;
}

.firework::before {
    transform: translate(-50%, -50%) rotate(25deg) !important; 
}

.firework::after {
    transform: translate(-50%, -50%) rotate(-37deg) !important;
}

</style>
